<template>
    <Head :title="`Asset: ${asset.name}`" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Back Button -->
                <div class="mb-4">
                    <Link
                        :href="route('assets.index')"
                        class="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Assets
                    </Link>
                </div>

                <!-- Asset Header -->
                <div class="bg-white rounded-lg shadow overflow-hidden mb-6">
                    <div class="p-6">
                        <div class="flex flex-col md:flex-row gap-6">
                            <!-- Asset Image -->
                            <div class="flex-shrink-0">
                                <div v-if="asset.image" class="w-48 h-48 rounded-lg overflow-hidden">
                                    <img
                                        :src="asset.image_url"
                                        :alt="asset.name"
                                        class="w-full h-full object-cover"
                                    />
                                </div>
                                <div v-else class="w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                                    <svg class="w-20 h-20 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                </div>
                            </div>

                            <!-- Asset Info -->
                            <div class="flex-1">
                                <div class="flex items-start justify-between">
                                    <div>
                                        <p class="text-sm text-gray-500">{{ asset.asset_tag }}</p>
                                        <div class="flex items-center gap-3 mt-1">
                                            <h1 class="text-3xl font-bold text-gray-900">{{ asset.name }}</h1>
                                            <InfoButton title="Asset Details & Management" variant="default">
                                                <p>
                                                    <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                                    The Asset Details page is your comprehensive view for a single asset. It consolidates all
                                                    information, maintenance history, work orders, and documentation in one place. Use the
                                                    tabs to navigate between different aspects of the asset's lifecycle, from basic details
                                                    to maintenance scheduling and work order history.
                                                </p>
                                                <p>
                                                    <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                                    • <strong>Navigation:</strong> Use the tabs to switch between Details, Maintenance, Work Orders,
                                                      Documents, and History views<br>
                                                    • <strong>Quick Actions:</strong> Use the header buttons to generate QR codes, edit asset
                                                      information, or create new work orders directly<br>
                                                    • <strong>Status Monitoring:</strong> The header displays current operational status, criticality
                                                      level, and key performance metrics<br>
                                                    • <strong>Details Tab:</strong> View comprehensive asset information organized into Basic
                                                      Information, Equipment Details, Financial Information, and Maintenance Configuration sections<br>
                                                    • <strong>Maintenance Tab:</strong> Access PM schedules and maintenance history. Note that PM
                                                      schedules are created through PM Plans (Settings → Maintenance → PM Plans)<br>
                                                    • <strong>Work Orders Tab:</strong> View all related work orders and create new maintenance requests<br>
                                                    • <strong>Documents Tab:</strong> Store and access manuals, warranties, and maintenance procedures<br>
                                                    • <strong>History Tab:</strong> Track all changes and updates made to the asset
                                                </p>
                                                <p>
                                                    <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                                    • Use <strong>QR codes</strong> to enable mobile access to asset information in the field<br>
                                                    • Monitor <strong>warranty expiration</strong> in the Details tab to schedule warranty work before it expires<br>
                                                    • Check the <strong>Quick Stats</strong> for instant visibility into maintenance costs and downtime<br>
                                                    • Review <strong>maintenance history</strong> regularly to identify recurring issues<br>
                                                    • Keep <strong>documents up to date</strong> so technicians have access to current manuals and procedures<br>
                                                    • Link <strong>parent-child assets</strong> to track components and subsystems separately
                                                </p>
                                            </InfoButton>
                                        </div>
                                        <div class="mt-2 flex items-center space-x-3">
                                            <span
                                                :class="[
                                                    'px-3 py-1 text-sm font-medium rounded-full',
                                                    getStatusClass(asset.status)
                                                ]"
                                            >
                                                {{ asset.status_label }}
                                            </span>
                                            <span
                                                :class="[
                                                    'px-3 py-1 text-sm font-medium rounded-full',
                                                    getCriticalityClass(asset.criticality)
                                                ]"
                                            >
                                                {{ asset.criticality_label }} Criticality
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Action Buttons -->
                                    <div class="flex items-center space-x-2">
                                        <a
                                            :href="route('assets.qr-code', asset.id)"
                                            class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                        >
                                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                            </svg>
                                            QR Code
                                        </a>
                                        <Link
                                            v-if="$page.props.auth.permissions.includes('maintenance.assets.edit')"
                                            :href="route('assets.edit', asset.id)"
                                            class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                        >
                                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            Edit
                                        </Link>
                                        <Link
                                            :href="route('assets.create-work-order', asset.id)"
                                            class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                        >
                                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                            </svg>
                                            Create Work Order
                                        </Link>
                                    </div>
                                </div>

                                <!-- Quick Stats -->
                                <div class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div class="border-l-4 border-green-500 pl-4">
                                        <p class="text-sm text-gray-600">Total Maintenance Cost</p>
                                        <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                                            ${{ formatCurrency(metrics.total_maintenance_cost) }}
                                        </p>
                                    </div>
                                    <div class="border-l-4 border-yellow-500 pl-4">
                                        <p class="text-sm text-gray-600">Total Downtime</p>
                                        <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                                            {{ metrics.total_downtime_hours }} hrs
                                        </p>
                                    </div>
                                    <div class="border-l-4 border-blue-500 pl-4">
                                        <p class="text-sm text-gray-600">Work Orders</p>
                                        <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                                            {{ metrics.work_order_count }}
                                        </p>
                                    </div>
                                    <div class="border-l-4 border-purple-500 pl-4">
                                        <p class="text-sm text-gray-600">Asset Age</p>
                                        <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                                            {{ metrics.age_in_days }} days
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tabs -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="border-b border-gray-200">
                        <nav class="-mb-px flex space-x-8 px-6">
                            <button
                                v-for="tab in tabs"
                                :key="tab.id"
                                @click="activeTab = tab.id"
                                :class="[
                                    'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
                                    activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                ]"
                            >
                                {{ tab.label }}
                            </button>
                        </nav>
                    </div>

                    <!-- Tab Content -->
                    <div class="p-6">
                        <!-- Details Tab -->
                        <div v-if="activeTab === 'details'">
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <!-- Basic Information -->
                                <div>
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>
                                    <dl class="space-y-3">
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Category</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.category?.name || '-' }}</dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Location</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.location?.name || '-' }}</dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Assigned To</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ assignedUser?.name || 'Unassigned' }}</dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Department</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.department || '-' }}</dd>
                                        </div>
                                    </dl>
                                </div>

                                <!-- Equipment Details -->
                                <div>
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Equipment Details</h3>
                                    <dl class="space-y-3">
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Manufacturer</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.manufacturer || '-' }}</dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Model</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.model || '-' }}</dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Serial Number</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.serial_number || '-' }}</dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Barcode</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.barcode || '-' }}</dd>
                                        </div>
                                    </dl>
                                </div>

                                <!-- Financial Information -->
                                <div>
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Financial Information</h3>
                                    <dl class="space-y-3">
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Purchase Date</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.purchase_date || '-' }}</dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Purchase Cost</dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ asset.purchase_cost ? `$${formatCurrency(asset.purchase_cost)}` : '-' }}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Current Value</dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ asset.current_value ? `$${formatCurrency(asset.current_value)}` : '-' }}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Warranty Expiry</dt>
                                            <dd class="mt-1 text-sm">
                                                <span :class="isWarrantyExpired() ? 'text-red-600' : 'text-gray-900'">
                                                    {{ asset.warranty_expiry_date || '-' }}
                                                </span>
                                                <span v-if="metrics.warranty_days_remaining !== null" class="ml-2 text-xs text-gray-500">
                                                    ({{ metrics.warranty_days_remaining }} days {{ metrics.warranty_days_remaining >= 0 ? 'remaining' : 'expired' }})
                                                </span>
                                            </dd>
                                        </div>
                                    </dl>
                                </div>

                                <!-- Maintenance Configuration -->
                                <div>
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Maintenance Configuration</h3>
                                    <dl class="space-y-3">
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Last Maintenance</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.last_maintenance_date || 'Never' }}</dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Next Maintenance</dt>
                                            <dd class="mt-1 text-sm">
                                                <span :class="isMaintenanceDue() ? 'text-red-600 font-medium' : 'text-gray-900'">
                                                    {{ asset.next_maintenance_date || 'Not scheduled' }}
                                                </span>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Meter Type</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ asset.meter_type || 'None' }}</dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>

                            <!-- Specifications -->
                            <div v-if="asset.specifications" class="mt-6">
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Specifications</h3>
                                <div class="bg-gray-50 rounded-lg p-4">
                                    <dl class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div v-for="(value, key) in asset.specifications" :key="key">
                                            <dt class="text-sm font-medium text-gray-500">{{ key }}</dt>
                                            <dd class="mt-1 text-sm text-gray-900">{{ value }}</dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>

                            <!-- Notes -->
                            <div v-if="asset.notes" class="mt-6">
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h3>
                                <p class="text-sm text-gray-600 whitespace-pre-wrap">{{ asset.notes }}</p>
                            </div>
                        </div>

                        <!-- Maintenance Tab -->
                        <div v-if="activeTab === 'maintenance'">
                            <div class="flex items-center gap-3 mb-6">
                                <div>
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Preventive Maintenance Overview</h3>
                                    <p class="text-sm text-gray-600">View upcoming PM schedules and maintenance history</p>
                                </div>
                                <InfoButton title="PM Schedules & Maintenance" variant="default">
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">About this tab</strong><br>
                                        The Maintenance tab shows all preventive maintenance (PM) schedules linked to this asset and
                                        tracks recent maintenance history. PM schedules automatically generate work orders based on
                                        time intervals (calendar-based) or meter readings (usage-based). This proactive approach
                                        reduces unexpected breakdowns and extends asset life.
                                    </p>
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">How to add PM schedules</strong><br>
                                        <strong>Important:</strong> You cannot create PM schedules directly on this page. To add PM
                                        schedules to this asset, follow these steps:<br>
                                        1. <strong>Create a PM Plan</strong> - Navigate to Settings → Maintenance → PM Plans<br>
                                        2. <strong>Define the plan</strong> - Set up the maintenance tasks, frequency (daily, weekly,
                                           monthly, etc.), and task checklist<br>
                                        3. <strong>Link to assets</strong> - When creating or editing the PM Plan, select this asset
                                           in the "Assets" field<br>
                                        4. <strong>Automatic scheduling</strong> - Once linked, the system automatically creates a PM
                                           schedule for this asset and generates work orders based on the plan's frequency<br>
                                        <br>
                                        <em>Example: Create a "Monthly HVAC Filter Check" PM Plan and link it to all HVAC assets. The
                                        system will automatically schedule monthly work orders for each asset.</em>
                                    </p>
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                        • <strong>Plan ahead</strong> - PM Plans are reusable templates; create them once and link
                                          to multiple similar assets<br>
                                        • <strong>Monitor upcoming schedules</strong> - This tab shows when PM work orders will be
                                          generated, allowing you to plan resources<br>
                                        • <strong>Review history</strong> - Track completed maintenance to identify trends and optimize
                                          PM intervals<br>
                                        • <strong>Calendar vs. meter-based</strong> - Use calendar-based schedules for time-dependent
                                          tasks (e.g., monthly inspections) and meter-based for usage-dependent tasks (e.g., every
                                          1,000 operating hours)<br>
                                        • <strong>Work order integration</strong> - PM schedules automatically create work orders in
                                          "Open" status, ready for assignment
                                    </p>
                                </InfoButton>
                            </div>
                            <div class="space-y-6">
                                <!-- Upcoming PM Plans -->
                                <div v-if="upcomingPm.length > 0">
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Upcoming Preventive Maintenance</h3>
                                    <div class="space-y-3">
                                        <div
                                            v-for="pm in upcomingPm"
                                            :key="pm.id"
                                            class="flex items-center justify-between p-4 bg-blue-50 rounded-lg"
                                        >
                                            <div>
                                                <p class="font-medium text-gray-900">{{ pm.name }}</p>
                                                <p class="text-sm text-gray-600 mt-1">Next due: {{ pm.next_due_date }}</p>
                                            </div>
                                            <span class="px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
                                                {{ pm.frequency_value }} {{ pm.frequency_unit }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Recent Maintenance History -->
                                <div>
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Maintenance History</h3>
                                    <div v-if="maintenanceHistory.length > 0" class="space-y-4">
                                        <div
                                            v-for="wo in maintenanceHistory"
                                            :key="wo.id"
                                            class="border-l-4 border-gray-300 pl-4 py-2"
                                        >
                                            <div class="flex items-start justify-between">
                                                <div>
                                                    <Link
                                                        :href="route('work-orders.show', wo.id)"
                                                        class="font-medium text-blue-600 hover:text-blue-800"
                                                    >
                                                        {{ wo.work_order_number }}
                                                    </Link>
                                                    <p class="text-sm text-gray-600 mt-1">{{ wo.title }}</p>
                                                    <p class="text-xs text-gray-500 mt-1">
                                                        {{ formatDate(wo.actual_end) }} • {{ wo.type_label }}
                                                    </p>
                                                </div>
                                                <span
                                                    :class="[
                                                        'px-2 py-1 text-xs font-medium rounded-full',
                                                        getWorkOrderStatusClass(wo.status)
                                                    ]"
                                                >
                                                    {{ wo.status_label }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <p v-else class="text-sm text-gray-500">No maintenance history available.</p>
                                </div>
                            </div>
                        </div>

                        <!-- Work Orders Tab -->
                        <div v-if="activeTab === 'work-orders'">
                            <div class="flex justify-between items-center mb-4">
                                <div class="flex items-center gap-3">
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Work Orders</h3>
                                    <InfoButton title="Asset Work Orders" variant="default" size="sm">
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">About this tab</strong><br>
                                            This tab shows all work orders associated with this asset, including preventive maintenance
                                            (generated from PM schedules), corrective maintenance (repairs), inspections, and emergency
                                            work. Track the complete maintenance history to identify reliability issues and optimize
                                            maintenance strategies.
                                        </p>
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                            • <strong>View all work orders:</strong> See open, in-progress, and completed work orders
                                              related to this asset<br>
                                            • <strong>Create new work orders:</strong> Click "New Work Order" to create corrective,
                                              preventive, or emergency maintenance requests<br>
                                            • <strong>Track status:</strong> Work orders display status badges (Open, Assigned, In Progress,
                                              Completed) to show current state<br>
                                            • <strong>Click for details:</strong> Select any work order to view full details, add notes,
                                              attach documents, or update status
                                        </p>
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                            • Monitor <strong>recurring issues</strong> - Multiple work orders for the same problem may
                                              indicate a need for replacement or process change<br>
                                            • Review <strong>maintenance costs</strong> - High work order frequency may justify preventive
                                              upgrades<br>
                                            • Use <strong>priority levels</strong> - Emergency and high-priority work orders appear at
                                              the top for quick visibility
                                        </p>
                                    </InfoButton>
                                </div>
                                <Link
                                    :href="route('assets.create-work-order', asset.id)"
                                    class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    New Work Order
                                </Link>
                            </div>

                            <!-- Work Orders List -->
                            <div v-if="openWorkOrders.length > 0" class="space-y-3">
                                <div
                                    v-for="wo in openWorkOrders"
                                    :key="wo.id"
                                    class="border rounded-lg p-4 hover:shadow-md transition-shadow"
                                >
                                    <div class="flex items-start justify-between">
                                        <div class="flex-1">
                                            <Link
                                                :href="route('work-orders.show', wo.id)"
                                                class="font-medium text-blue-600 hover:text-blue-800"
                                            >
                                                {{ wo.work_order_number }}
                                            </Link>
                                            <p class="text-sm text-gray-600 mt-1">{{ wo.title }}</p>
                                            <div class="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                                <span>{{ wo.type_label }}</span>
                                                <span>•</span>
                                                <span>{{ wo.priority_label }}</span>
                                                <span>•</span>
                                                <span>Created {{ formatDate(wo.created_at) }}</span>
                                            </div>
                                        </div>
                                        <span
                                            :class="[
                                                'px-2 py-1 text-xs font-medium rounded-full',
                                                getWorkOrderStatusClass(wo.status)
                                            ]"
                                        >
                                            {{ wo.status_label }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <p v-else class="text-sm text-gray-500">No open work orders for this asset.</p>
                        </div>

                        <!-- Documents Tab -->
                        <div v-if="activeTab === 'documents'">
                            <div class="text-center py-12">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                <h3 class="mt-2 text-sm font-medium text-gray-900">No documents</h3>
                                <p class="mt-1 text-sm text-gray-500">Upload manuals, warranties, and other documents.</p>
                                <div class="mt-6">
                                    <button class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                        </svg>
                                        Upload Document
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- History Tab -->
                        <div v-if="activeTab === 'history'">
                            <div class="text-center py-12">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h3 class="mt-2 text-sm font-medium text-gray-900">No history available</h3>
                                <p class="mt-1 text-sm text-gray-500">Asset change history will appear here.</p>
                            </div>
                        </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    asset: Object,
    assignedUser: Object,
    maintenanceHistory: Array,
    openWorkOrders: Array,
    upcomingPm: Array,
    metrics: Object,
});

const activeTab = ref('details');

const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'maintenance', label: 'Maintenance' },
    { id: 'work-orders', label: 'Work Orders' },
    { id: 'documents', label: 'Documents' },
    { id: 'history', label: 'History' },
];

const getStatusClass = (status) => {
    const classes = {
        operational: 'bg-green-100 text-green-800',
        down: 'bg-red-100 text-red-800',
        maintenance: 'bg-yellow-100 text-yellow-800',
        retired: 'bg-gray-100 text-gray-800',
        disposed: 'bg-gray-100 text-gray-800',
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
};

const getCriticalityClass = (criticality) => {
    const classes = {
        low: 'bg-blue-100 text-blue-800',
        medium: 'bg-yellow-100 text-yellow-800',
        high: 'bg-orange-100 text-orange-800',
        critical: 'bg-red-100 text-red-800',
    };
    return classes[criticality] || 'bg-gray-100 text-gray-800';
};

const getWorkOrderStatusClass = (status) => {
    const classes = {
        draft: 'bg-gray-100 text-gray-800',
        open: 'bg-blue-100 text-blue-800',
        assigned: 'bg-cyan-100 text-cyan-800',
        in_progress: 'bg-yellow-100 text-yellow-800',
        on_hold: 'bg-orange-100 text-orange-800',
        pending_review: 'bg-purple-100 text-purple-800',
        completed: 'bg-green-100 text-green-800',
        closed: 'bg-gray-100 text-gray-800',
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
};

const isMaintenanceDue = () => {
    if (!props.asset.next_maintenance_date) return false;
    return new Date(props.asset.next_maintenance_date) <= new Date();
};

const isWarrantyExpired = () => {
    if (!props.asset.warranty_expiry_date) return false;
    return new Date(props.asset.warranty_expiry_date) < new Date();
};

const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};
</script>
